import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, CheckCircle, Loader2, ClipboardList, MessageSquare, XCircle,
} from 'lucide-react';
import { proposalsApi } from '../utils/api';
import ProposalCommentThread from '../components/ProposalCommentThread';

// Client-facing remediation PLAN — the scope-only, no-prices step before the
// priced remediation proposal. Shows exactly what the security risk
// assessment says needs doing, with capital purchases (servers, workstations
// too old to encrypt, network gear) broken out so there are no big shockers
// when pricing arrives. Approving the plan is informational — it tells us to
// build the priced proposal off this exact scope; nothing is billed here.

const cell = { padding: '8px 10px', borderBottom: '1px solid #eef0f3', fontSize: 14 };
const groupHeader = {
  ...cell, background: '#f8fafc', fontWeight: 600, fontSize: 13,
  color: '#475569', textTransform: 'uppercase', letterSpacing: '0.03em',
};

export default function RemediationPlan() {
  const { pathId } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [acting, setActing] = useState(false);
  const [actionMessage, setActionMessage] = useState(null);
  const commentThreadRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const d = await proposalsApi.getRemediationPlan(pathId);
      setData(d);
      setError('');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [pathId]);

  useEffect(() => { load(); }, [load]);

  const handleApprove = async () => {
    if (!window.confirm(
      'Approve this remediation plan? Nothing is billed now — we will follow up with an itemized proposal priced to this exact scope.'
    )) return;
    setActing(true);
    setActionMessage(null);
    try {
      await proposalsApi.acceptRemediationPlan(pathId);
      setActionMessage({ type: 'success', text: 'Plan approved — we will send the priced proposal next.' });
      await load();
    } catch (e) {
      setActionMessage({ type: 'error', text: e.message });
    } finally {
      setActing(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <Loader2 className="spinner-large" size={48} />
        <p>Loading remediation plan…</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="proposal-page">
        <div className="proposal-topbar">
          <button onClick={() => navigate('/')} className="back-nav-button">
            <ArrowLeft size={16} /> Dashboard
          </button>
        </div>
        <div className="error-message">{error || 'Remediation plan not found.'}</div>
      </div>
    );
  }

  const items = Array.isArray(data.items) ? data.items : [];
  const capitalItems = items.filter((i) => i.capital);
  const workItems = items.filter((i) => !i.capital);
  const approved = !!data.accepted_at;

  const renderRows = (rows) => rows.map((r, i) => (
    <tr key={i}>
      <td style={{ ...cell, width: 180, color: '#475569' }}>{r.category}</td>
      <td style={cell}>{r.description}</td>
      <td style={{ ...cell, textAlign: 'right', width: 60 }}>{r.quantity}</td>
    </tr>
  ));

  return (
    <div className="proposal-page">
      <div className="proposal-topbar">
        <button onClick={() => navigate('/')} className="back-nav-button">
          <ArrowLeft size={16} /> Dashboard
        </button>
      </div>

      <header className="proposal-header">
        <div>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <ClipboardList size={24} /> Remediation Plan for {data.customer_name}
          </h1>
          {data.shared_at && (
            <div className="proposal-header-meta">
              Shared {new Date(data.shared_at).toLocaleDateString()}
            </div>
          )}
        </div>
        {approved && (
          <span className="proposal-status-pill" style={{ color: '#16a34a', background: '#dcfce7' }}>
            Approved
          </span>
        )}
      </header>

      <div className="proposal-banner" style={{ background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e3a8a' }}>
        <ClipboardList size={18} />
        <div style={{ flex: 1 }}>
          <strong>Scope first, pricing second.</strong>{' '}
          This is everything your security risk assessment says needs fixing —
          laid out before any dollar figure, so there are no surprises. Larger
          equipment purchases are listed separately below. Once you approve the
          scope, we'll send an itemized proposal priced to exactly this list.
        </div>
      </div>

      {actionMessage && (
        <div className={`proposal-banner ${actionMessage.type === 'success' ? 'proposal-banner-success' : 'proposal-banner-error'}`}>
          {actionMessage.type === 'success' ? <CheckCircle size={18} /> : <XCircle size={18} />}
          <span>{actionMessage.text}</span>
        </div>
      )}

      {approved && !actionMessage && (
        <div className="proposal-banner proposal-banner-success">
          <CheckCircle size={18} />
          <div>
            <strong>Plan approved</strong> on {new Date(data.accepted_at).toLocaleDateString()}.
            {' '}Your priced remediation proposal is on its way.
          </div>
        </div>
      )}

      <section className="proposal-body">
        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
          <thead>
            <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
              <th style={cell}>Category</th>
              <th style={cell}>What needs doing</th>
              <th style={{ ...cell, textAlign: 'right' }}>Qty</th>
            </tr>
          </thead>
          <tbody>
            {capitalItems.length > 0 ? (
              <>
                <tr><td style={groupHeader} colSpan={3}>Remediation work</td></tr>
                {renderRows(workItems)}
                <tr><td style={groupHeader} colSpan={3}>Capital purchases (equipment)</td></tr>
                {renderRows(capitalItems)}
              </>
            ) : renderRows(items)}
          </tbody>
        </table>

        {data.notes && (
          <p style={{ marginTop: 14, color: '#475569', fontSize: 14 }}>
            <strong>Notes:</strong> {data.notes}
          </p>
        )}
      </section>

      {!approved && (
        <section className="proposal-actions">
          <div className="proposal-action-buttons">
            <button
              type="button"
              className="proposal-action-btn proposal-accept-btn"
              onClick={handleApprove}
              disabled={acting}
            >
              {acting ? <Loader2 className="spinner" size={16} /> : <CheckCircle size={16} />}
              {' '}Approve Plan Scope
            </button>
            <button
              type="button"
              className="proposal-action-btn proposal-question-btn"
              onClick={() => commentThreadRef.current?.focus()}
              disabled={acting}
            >
              <MessageSquare size={16} /> Ask a Question
            </button>
          </div>
          <p style={{ fontSize: 13, color: '#64748b', marginTop: 8 }}>
            Approving the plan doesn't cost anything — it just confirms the
            scope so we can price it for you.
          </p>
        </section>
      )}

      <ProposalCommentThread ref={commentThreadRef} pathId={pathId} />
    </div>
  );
}
